import React from 'react'
import { OpenScoreboardData } from '../../services/apiTypes'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Text from '../../components/Text'
import { colors } from '../../styles/_var'
import ScoreboardResult from './ScoreboardResult'
import { PaginationInfo, ScoreboardParticipant } from '../../utils/types'

type ScoreboardContentProps = {
  scoreboard_id: number
  hasWriteAccess: boolean
  participants?: ScoreboardParticipant[]
  scoreboardResults: OpenScoreboardData[]
  resultsPage: number
  resultsPaginationInfo?: PaginationInfo
  hasPreviousResultsPage?: boolean
  hasNextResultsPage?: boolean
  gotoNextResultsPage: () => void
  gotoPreviousResultsPage: () => void
  isLoading?: boolean
  isError?: boolean
}

const ScoreboardContent = ({
  scoreboard_id,
  hasWriteAccess,
  participants,
  scoreboardResults,
  resultsPage,
  resultsPaginationInfo,
  hasPreviousResultsPage,
  hasNextResultsPage,
  gotoNextResultsPage,
  gotoPreviousResultsPage,
  isLoading,
  isError,
}: ScoreboardContentProps) => {
  const [expandedRows, setExpandedRows] = React.useState<Record<number, boolean>>({})

  const toggleRow = (resultId: number) => {
    setExpandedRows(prev => ({...prev, [resultId]: !prev[resultId]}))
  }

  if (isLoading) {
    return (
      <Text center variant="medium" weight="bold" useDiv>
        Loading scoreboard results...
      </Text>
    )
  }

  if (isError) {
    return (
      <Text center variant="medium" weight="bold" useDiv color={colors.danger}>
        Failed to load scoreboard results
      </Text>
    )
  }

  return (
    <Box id="open-scoreboard-results">
      {!scoreboardResults.length && (
        <Text center variant="medium" weight="bold" useDiv color={colors.danger}>
          No scoreboard results yet
        </Text>
      )}
      {scoreboardResults.map((result) => (
        <ScoreboardResult
          key={result.id}
          result={result}
          scoreboard_id={scoreboard_id}
          hasWriteAccess={hasWriteAccess}
          page={resultsPage}
          participants={participants}
          isExpanded={!!expandedRows[result.id]}
          onToggle={() => toggleRow(result.id)}
        />
      ))}
      {(resultsPaginationInfo?.total_pages || 0) > 1 && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5}>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasPreviousResultsPage}
            onClick={gotoPreviousResultsPage}
          >
            Previous
          </Button>
          <Text variant="small" useDiv color={colors.darkgrey}>
            Page {resultsPage} of {resultsPaginationInfo?.total_pages || 1}
          </Text>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasNextResultsPage}
            onClick={gotoNextResultsPage}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ScoreboardContent